import {
  STAFF_CATALOG_PLACE_BOUNDS,
  type StaffCatalogItemRecord,
  type StaffCatalogPlaceCandidate,
  type StaffCatalogPlaceCorrection,
  type StaffCatalogPlaceSearchBody,
  type StaffCatalogPlaceSearchResult,
} from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { Button } from "../../../ui/button";
import { cn } from "../../../ui/cn";
import { CheckboxField, SelectField, TextField } from "../../../ui/fields";
import { RadioInput } from "../../../ui/radio";
import { StatusBadge } from "../../../ui/status-badge";
import { StatusMessage } from "../../../ui/status-message";
import {
  groupBox,
  groupLegend,
  hintText,
  plainList,
  subheading,
  subsection,
} from "../../components/styles";
import { formatDateTime, humanizeKey } from "../../labels";
import { useCatalogRuntime } from "../../runtime";
import { distanceMeters, formatDistance } from "../distance";
import { describePlaceError } from "../errors";
import { isHttpsUrl } from "../parts";
import { placeKeys } from "../queries";
import { attribution, externalLink } from "../styles";
import { MatchDialog } from "./PlaceDialogs";

// The search panel. It holds the answer of one provider search in this component's state and nowhere
// else: not in the query cache, not in storage, not in the address, not in a message. When the panel
// closes, or the page is left, the state is gone. The panel has NO control that copies a result into a
// field: a staff member picks the place that IS the item (the server saves only its Place ID), and types
// any canonical value from an independent source in the location section.
//
// Attribution (Google's terms): "Google Maps" next to the list, and the name of every third-party
// provider the answer carries. No map is shown.

type SearchState =
  | Readonly<{ phase: "idle" }>
  | Readonly<{ phase: "searching" }>
  | Readonly<{ phase: "failed"; message: string }>
  | Readonly<{ phase: "done"; result: StaffCatalogPlaceSearchResult }>;

const RADIUS_OPTIONS = [
  { value: "1000", label: "1 km" },
  { value: "5000", label: "5 km" },
  { value: "25000", label: "25 km" },
] as const;

type PlaceSearchPanelProps = Readonly<{
  item: StaffCatalogItemRecord;
  /** The item has a match already: choosing a result is a correction and needs a reason. */
  hasCurrent: boolean;
  lookupAvailable: boolean;
  /** Runs P3. Returns a sentence to show when it failed, or `null` when it worked. */
  onSelect: (
    result: StaffCatalogPlaceSearchResult,
    candidate: StaffCatalogPlaceCandidate,
    correction: StaffCatalogPlaceCorrection | undefined,
  ) => Promise<string | null>;
  onClose: () => void;
}>;

export function PlaceSearchPanel({
  item,
  hasCurrent,
  lookupAvailable,
  onSelect,
  onClose,
}: PlaceSearchPanelProps) {
  const { api, now } = useCatalogRuntime();
  const queryClient = useQueryClient();
  const idBase = useId();
  const defaultText = [item.name, item.locality].filter(Boolean).join(" ").slice(0, 200);
  const [text, setText] = useState(defaultText);
  const [textError, setTextError] = useState<string | undefined>();
  const [nearSaved, setNearSaved] = useState(false);
  const [radius, setRadius] = useState("5000");
  const [state, setState] = useState<SearchState>({ phase: "idle" });
  const [chosen, setChosen] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const resultsHeading = useRef<HTMLHeadingElement>(null);
  const failureNote = useRef<HTMLParagraphElement>(null);
  const textInput = useRef<HTMLDivElement>(null);
  const inFlight = useRef<AbortController | null>(null);
  const closed = useRef(false);

  const savedPoint =
    item.latitude !== null && item.longitude !== null
      ? { latitude: item.latitude, longitude: item.longitude }
      : null;

  useEffect(() => {
    closed.current = false;
    // The panel opens from a button that sits before it in the tab order: put the focus in the panel, so a
    // keyboard user does not tab past the other action buttons to reach the field.
    textInput.current?.querySelector("input")?.focus();
    return () => {
      // The panel is leaving: nothing may update it, and the answer is dropped with its state.
      closed.current = true;
      inFlight.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (state.phase === "done") resultsHeading.current?.focus();
    if (state.phase === "failed") failureNote.current?.focus();
  }, [state]);

  async function search(event: FormEvent) {
    event.preventDefault();
    if (inFlight.current !== null) return;
    const trimmed = text.trim();
    if (trimmed.length > STAFF_CATALOG_PLACE_BOUNDS.searchText) {
      setTextError(`Use at most ${STAFF_CATALOG_PLACE_BOUNDS.searchText} characters.`);
      textInput.current?.querySelector("input")?.focus();
      return;
    }
    setTextError(undefined);
    const body: StaffCatalogPlaceSearchBody = {
      ...(trimmed === "" ? {} : { text: trimmed }),
      ...(nearSaved && savedPoint !== null
        ? { near: { ...savedPoint, radiusMeters: Number(radius) } }
        : {}),
    };
    const controller = new AbortController();
    inFlight.current = controller;
    setChosen(null);
    setState({ phase: "searching" });
    try {
      const result = await api.searchPlace(item.id, body, { signal: controller.signal });
      if (!closed.current) setState({ phase: "done", result });
    } catch (error) {
      if (!closed.current) {
        const failure = describePlaceError(error, { action: "search", lookupAvailable });
        setState({ phase: "failed", message: failure.message });
      }
    } finally {
      inFlight.current = null;
      // The server wrote a lookup row (and maybe a new state). Read the place view again.
      void queryClient.invalidateQueries({ queryKey: placeKeys.all(item.id) });
    }
  }

  const result = state.phase === "done" ? state.result : null;
  const candidate = useMemo(
    () => result?.candidates.find((entry) => entry.placeId === chosen) ?? null,
    [result, chosen],
  );
  const expired = result !== null && result.expiresAt.getTime() <= now().getTime();
  const providerNames = useMemo(() => {
    const seen = new Map<string, string | null>();
    for (const entry of result?.candidates ?? []) {
      for (const attribution of entry.attributions) {
        // "Google Maps" is already named in the first words of the line.
        if (attribution.provider.trim().toLowerCase() === "google maps") continue;
        if (!seen.has(attribution.provider) || seen.get(attribution.provider) === null) {
          seen.set(attribution.provider, attribution.uri);
        }
      }
    }
    return [...seen.entries()];
  }, [result]);

  const statusText =
    state.phase === "searching"
      ? "Searching…"
      : result === null
        ? ""
        : result.candidates.length === 0
          ? "No places found."
          : `${result.candidates.length} ${result.candidates.length === 1 ? "place" : "places"} found.`;

  return (
    <section className={cn(subsection, "min-w-0")} aria-labelledby={`${idBase}-title`}>
      <h3 id={`${idBase}-title`} className={subheading}>
        Find the place
      </h3>
      <form noValidate onSubmit={search} className="grid gap-4" aria-label="Search for the place">
        <div ref={textInput}>
          <TextField
            id={`${idBase}-text`}
            label="Search words"
            value={text}
            onChange={setText}
            type="search"
            maxLength={STAFF_CATALOG_PLACE_BOUNDS.searchText}
            error={textError}
            hint="The name and the town work best. These words are sent to Google Maps."
            disabled={!lookupAvailable}
          />
        </div>
        {savedPoint === null ? null : (
          <div className="grid gap-2">
            <CheckboxField
              id={`${idBase}-near`}
              label="Prefer results near the saved point"
              checked={nearSaved}
              onChange={setNearSaved}
              disabled={!lookupAvailable}
            />
            {nearSaved ? (
              <SelectField
                id={`${idBase}-radius`}
                label="Search radius"
                value={radius}
                onChange={setRadius}
                options={RADIUS_OPTIONS}
                disabled={!lookupAvailable}
              />
            ) : null}
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button type="submit" disabled={!lookupAvailable || state.phase === "searching"}>
            {state.phase === "searching" ? "Searching…" : "Search"}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close the search
          </Button>
        </div>
      </form>

      <p role="status" aria-live="polite" className={cn(hintText, "min-h-6")}>
        {statusText}
      </p>

      {state.phase === "failed" ? (
        <StatusMessage message={{ tone: "error", text: state.message }} focusRef={failureNote} />
      ) : null}

      {result === null ? null : (
        <div className="grid min-w-0 gap-4" aria-busy={state.phase === "searching"}>
          <h4 ref={resultsHeading} tabIndex={-1} className={subheading}>
            Search results
          </h4>
          <p className={attribution}>
            Results from Google Maps.
            {providerNames.length > 0 ? " Also provided by: " : ""}
            {providerNames.map(([provider, uri], index) => (
              <span key={provider}>
                {index > 0 ? ", " : ""}
                {isHttpsUrl(uri) ? (
                  <a className={externalLink} href={uri} target="_blank" rel="noopener noreferrer">
                    {provider}
                  </a>
                ) : (
                  provider
                )}
              </span>
            ))}
          </p>
          <p className={cn(attribution, "mt-2")} data-testid="place-staff-rule">
            Use Google only to choose the place. Take the address and the coordinates from the
            venue&apos;s own website or from a source Lafayette owns, and record that source. Do not
            copy them from Google.
          </p>
          {result.candidates.length === 0 ? (
            <p className={hintText}>
              Google Maps found no place for these words. Try other words, or mark the item as
              having no physical place.
            </p>
          ) : (
            <>
              <fieldset className={cn(groupBox, "min-w-0")}>
                <legend className={groupLegend}>Choose the place that is this item</legend>
                <ul className={plainList}>
                  {result.candidates.map((entry) => {
                    const distance =
                      savedPoint !== null && entry.point !== null
                        ? formatDistance(distanceMeters(savedPoint, entry.point))
                        : null;
                    const inputId = `${idBase}-candidate-${entry.rank}`;
                    return (
                      <li
                        key={entry.placeId}
                        className="relative grid min-h-11 grid-cols-[2.75rem_minmax(0,1fr)] items-start gap-x-2 rounded-sm border border-border px-2 py-1 has-[:checked]:border-2 has-[:checked]:border-primary has-[:checked]:bg-accent"
                      >
                        <RadioInput
                          id={inputId}
                          className="relative z-10 col-start-1 row-start-1 m-2.5 size-6"
                          name={`${idBase}-candidate`}
                          value={entry.placeId}
                          checked={chosen === entry.placeId}
                          onChange={() => setChosen(entry.placeId)}
                        />
                        {/* The label spans the whole row, behind the radio: a tap beside the mark chooses the result too. */}
                        <label
                          htmlFor={inputId}
                          className="col-span-full row-start-1 grid min-h-11 min-w-0 cursor-pointer content-center gap-0.5 pl-13 [overflow-wrap:anywhere]"
                        >
                          <span className="font-bold">{entry.displayName ?? "Unnamed place"}</span>
                          <span>{entry.formattedAddress ?? "No address given"}</span>
                          <span className="text-sm text-muted-foreground">
                            {entry.typeCodes.length > 0
                              ? entry.typeCodes.map((code) => humanizeKey(code)).join(", ")
                              : "No type given"}
                            {distance === null ? "" : ` · about ${distance} from the saved point`}
                          </span>
                          {entry.closedPermanently === true ? (
                            <StatusBadge
                              tone="warning"
                              className="justify-self-start whitespace-normal"
                            >
                              Permanently closed
                            </StatusBadge>
                          ) : null}
                        </label>
                        {isHttpsUrl(entry.mapsUrl) ? (
                          <a
                            className={cn(externalLink, "col-start-2 row-start-2")}
                            href={entry.mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open result ${entry.rank} in Google Maps`}
                          >
                            Open in Google Maps
                          </a>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </fieldset>
              <p className={hintText}>
                {expired
                  ? "These results have expired. Search again."
                  : `You can choose from this list until ${formatDateTime(result.expiresAt)}.`}
              </p>
              <div>
                <Button
                  type="button"
                  disabled={candidate === null || expired}
                  onClick={() => setConfirming(true)}
                >
                  {hasCurrent ? "Correct the match" : "Match this place"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {confirming && result !== null && candidate !== null ? (
        <MatchDialog
          candidate={candidate}
          replacing={hasCurrent}
          onConfirm={(correction) => onSelect(result, candidate, correction)}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </section>
  );
}
